import {
  Box,
  Tooltip,
  tooltipClasses,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Helper } from "../../utils/Helpers";
const { getGradientTextStyle } = new Helper();

export const CustomTooltip = styled(({ className, open, ...props }) => (
  <Tooltip
    {...props}
    arrow
    classes={{ popper: className }}
    // placement="bottom"
    open={open}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: { color: "white" },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "white",
    boxShadow: theme.shadows[5],
    borderRadius: 8,
  },
}));

//Formatting currency
export const formatCurrency = (value) =>
  value
    ? value.toLocaleString("en-IN", { style: "currency", currency: "INR" })
    : "-";

//Footer totals
export const renderFooterTotal = (table, fieldName) => {
  const total =
    parseFloat(table.getRowModel().rows[0]?.original?.[fieldName]) || 0;
  return total ? formatCurrency(total) : "";
};

//Currency fields
export const currencyCell = ({ cell }) =>
  formatCurrency(parseFloat(cell.getValue()));

// Common Currency Column Config
export const currencyColumnConfig = {
  size: 50,
  Cell: currencyCell,
  muiTableHeadCellProps: {
    align: "center",
  },
  muiTableBodyCellProps: {
    align: "center",
  },
  muiTableFooterCellProps: {
    align: "center",
  },
};

// RoomNumberCell Tooltip
export const RoomNumberCell = ({ cell }) => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const roomNumbers = cell.getValue();
  const roomArray = roomNumbers ? roomNumbers.split(",") : [];
  const firstRoom = roomArray[0] || "-";
  const isMultipleRooms = roomArray.length > 1;
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleTooltipToggle = () => {
    setTooltipOpen(!tooltipOpen);
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  return (
    <Box sx={{ textAlign: "center" }}>
      <Typography
        sx={{
          fontSize: { xs: "2.564vw", sm: "1.2vw", md: "0.695vw" },
          fontWeight: 400,
        }}
      >
        {isMultipleRooms ? "" : firstRoom}
      </Typography>

      {isMultipleRooms && (
        <CustomTooltip
          title={
            <>
              <Typography
                sx={{
                  fontSize: { xs: "2.564vw", sm: "1.439vw", md: "0.834vw" },
                  color: "#4F4F4F",
                }}
              >
                {t("pages.reports.total_room")} -&nbsp;
                <span style={{ ...getGradientTextStyle(palette) }}>
                  {roomArray.length.toString().padStart(2, "0")}
                </span>
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "2.564vw", sm: "1.439vw", md: "0.834vw" },
                  color: "#474747",
                }}
              >
                {roomNumbers}
              </Typography>
            </>
          }
          open={tooltipOpen}
        >
          <Box
            sx={{
              position: "relative",
              cursor: "pointer",
              width: { xs: "7.597vw", sm: "3.597vw", md: "2.083vw" },
              height: { xs: "7.597vw", sm: "3.597vw", md: "2.083vw" },
              border: isMultipleRooms ? `1px solid ${palette.border.gradient}` : "none",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "0 auto",
            }}
            onClick={handleTooltipToggle}
            onMouseEnter={() => setTooltipOpen(true)}
            onMouseLeave={handleTooltipClose}
          >
            <Typography
              sx={{
                fontSize: { xs: "2.564vw", sm: "1.2vw", md: "0.695vw" },
                fontWeight: 400,
              }}
            >
              {firstRoom}
            </Typography>
            {isMultipleRooms && (
              <Box
                sx={{
                  position: "absolute",
                  top: { xs: "-7%", sm: "-9%" },
                  right: { xs: "1%", sm: "-10%" },
                  height: { xs: "2vw", sm: "1.199vw", md: "0.694vw" },
                  width: { xs: "2vw", sm: "1.199vw", md: "0.694vw" },
                  background: palette.tab.gradient,
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.5vw", sm: "0.839vw", md: "0.486vw" },
                    color: "#FFFFFF",
                  }}
                >
                  {roomArray.length}
                </Typography>
              </Box>
            )}
          </Box>
        </CustomTooltip>
      )}
    </Box>
  );
};
export const AssignType = ({ cell }) => {
  const { t } = useTranslation();

  const status = t(
    `pages.reports.housekeeping.status_type.${cell.getValue()}`.toLowerCase()
  );

  return (
    <Typography
      sx={{
        fontSize: { xs: "2.564vw", sm: "1.2vw", md: "0.695vw" },
        fontWeight: 400,
        color: "#474747",
        textTransform: "capitalize",
      }}
    >
      {status || "-"}
    </Typography>
  );
};
export const ReceiptNumberCell = ({ cell }) => {
  const { t } = useTranslation();
  const receipt = cell.getValue();
  const receiptArray = receipt ? receipt.split(",") : [];
  const firstReceipt = receiptArray[0] || "—";
  const isMultipleReceipt = receiptArray.length > 1;
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleTooltipToggle = () => setTooltipOpen(!tooltipOpen);
  const handleTooltipClose = () => setTooltipOpen(false);

  return (
    <Box sx={{ textAlign: "center" }}>
      {!isMultipleReceipt ? (
        firstReceipt
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {firstReceipt}
          <CustomTooltip
            placement="right-start"
            arrow
            open={tooltipOpen}
            title={
              <>
                <Typography
                  sx={{
                    fontSize: { xs: "2.564vw", sm: "1.439vw", md: "0.833vw" },
                    fontWeight: 400,
                    color: "#D9A62E",
                  }}
                >
                  {t("pages.reports.sales.advance_receipt_number")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "2.564vw", sm: "1.439vw", md: "0.833vw" },
                    fontWeight: 400,
                    color: "#474747",
                  }}
                >
                  {receipt}
                </Typography>
              </>
            }
          >
            <VisibilityOutlinedIcon
              onClick={handleTooltipToggle}
              onMouseEnter={() => setTooltipOpen(true)}
              onMouseLeave={handleTooltipClose}
              sx={{
                cursor: "pointer",
                fontSize: { xs: "3vw", sm: "1.439vw", md: "0.833vw" },
                color: "rgba(228, 171, 35, 1)",
                ml: "3%",
              }}
            />
          </CustomTooltip>
        </Box>
      )}
    </Box>
  );
};
export const DepartureDateTimeCell = ({ cell }) => {
  const { t } = useTranslation();
  const departure = cell.getValue();
  const departureArray = departure ? departure.split(",") : [];
  const isMultipleDepartures = departureArray.length > 1;
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleTooltipToggle = () => setTooltipOpen(!tooltipOpen);
  const handleTooltipClose = () => setTooltipOpen(false);

  const formatDateTime = (datetimeStr) => {
    if (!datetimeStr) return "-";
    const isoDateStr = datetimeStr.replace(" ", "T");
    const date = new Date(isoDateStr);
    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const firstDeparture = departureArray[0]
    ? formatDateTime(departureArray[0])
    : "-";

  return (
    <Box sx={{ textAlign: "center" }}>
      {!isMultipleDepartures ? (
        firstDeparture
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {firstDeparture}
          <CustomTooltip
            placement="right-start"
            arrow
            open={tooltipOpen}
            title={
              <>
                <Typography
                  sx={{
                    fontSize: { xs: "2.564vw", sm: "1.439vw", md: "0.833vw" },
                    fontWeight: 400,
                    color: "#D9A62E",
                  }}
                >
                  {/* {t("pages.reports.foreigner.departure_date_time")} */}
                  Departure Date Time
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "2.564vw", sm: "1.439vw", md: "0.833vw" },
                    fontWeight: 400,
                    color: "#474747",
                  }}
                >
                  {departure}
                </Typography>
              </>
            }
          >
            <VisibilityOutlinedIcon
              onClick={handleTooltipToggle}
              onMouseEnter={() => setTooltipOpen(true)}
              onMouseLeave={handleTooltipClose}
              sx={{
                cursor: "pointer",
                fontSize: { xs: "3vw", sm: "1.439vw", md: "0.833vw" },
                color: "rgba(228, 171, 35, 1)",
                ml: "3%",
              }}
            />
          </CustomTooltip>
        </Box>
      )}
    </Box>
  );
};

export const ArrivalDateTimeCell = ({ cell }) => {
  const { t } = useTranslation();
  const arrival = cell.getValue();
  const arrivalArray = arrival ? arrival.split(",") : "-";
  const firstArrival = arrivalArray[0] ? formatDateTime(arrivalArray[0]) : "-";
  const isMultipleArrivals = arrivalArray.length > 1;
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const handleTooltipToggle = () => setTooltipOpen(!tooltipOpen);
  const handleTooltipClose = () => setTooltipOpen(false);

  return (
    <Box sx={{ textAlign: "center" }}>
      {!isMultipleArrivals ? (
        firstArrival
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {firstArrival}
          <CustomTooltip
            placement="right-start"
            arrow
            open={tooltipOpen}
            title={
              <>
                <Typography
                  sx={{
                    fontSize: { xs: "2.564vw", sm: "1.439vw", md: "0.833vw" },
                    fontWeight: 400,
                    color: "#D9A62E",
                  }}
                >
                  {/* {t("pages.reports.foreigner.arrival_date_time")} */}
                  Arriving Date
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "2.564vw", sm: "1.439vw", md: "0.833vw" },
                    fontWeight: 400,
                    color: "#474747",
                  }}
                >
                  {arrival}
                </Typography>
              </>
            }
          >
            <VisibilityOutlinedIcon
              onClick={handleTooltipToggle}
              onMouseEnter={() => setTooltipOpen(true)}
              onMouseLeave={handleTooltipClose}
              sx={{
                cursor: "pointer",
                fontSize: { xs: "3vw", sm: "1.439vw", md: "0.833vw" },
                color: "rgba(228, 171, 35, 1)",
                ml: "3%",
              }}
            />
          </CustomTooltip>
        </Box>
      )}
    </Box>
  );
};
export const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  const normalized = String(isoString).replace(" ", "T").split(".")[0];
  const utcString = normalized.endsWith("Z") ? normalized : normalized + "Z";
  const date = new Date(utcString);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};
export const formatDuration = (seconds) => {
  if (seconds == null) return "-";
  const days = Math.floor(seconds / 86400);
  const hrs = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
  return parts.join(" ");
};

export const DurationCell = ({ cell }) => {
  const seconds = cell.getValue();
  return (
    <Typography
      sx={{
        fontSize: { xs: "2.564vw", sm: "1.2vw", md: "0.695vw" },
        fontWeight: 400,
        color: "#474747",
      }}
    >
      {formatDuration(seconds)}
    </Typography>
  );
};

export const OCCUPANCY_STATUS_LABELS = {
  check_in: "Checkin",
  check_out: "Checkout",
  room_shift: "Room Shift",
  reserved: "Reserved",
  occupied: "Occupied",
  vacant: "Vacant",
};

export const OccupancyStatusCell = ({ cell }) => {
  const status = cell.getValue();
  const label = OCCUPANCY_STATUS_LABELS[status?.toLowerCase()] || status || "-";
  return (
    <Typography
      sx={{
        fontSize: { xs: "2.564vw", sm: "1.2vw", md: "0.695vw" },
        fontWeight: 400,
        color: "#474747",
      }}
    >
      {label}
    </Typography>
  );
};

export const formatDateDDMMMYYYY = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
